import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { LongButton } from '../../../components/buttons/longButton/LongButton'
import { Logo } from '../../../components/logo/Logo'
import { ModeSelector } from '../../../components/modeSelector/ModeSelector'
import { Premium } from '../../../components/premium/Premium'
import { translations } from '../../../configs/translations'
import { Language } from '../../../context/LanguageContext'
import { RaceTypes } from '../../../enums/RaceTypes'
import { areProfilesEqual, UserProfile } from '../../../models/UserProfile'
import { Vehicle } from '../../../models/Vehicle'
import AuthService from '../../../services/AuthService'
import GhostService from '../../../services/GhostService'
import PlayerService from '../../../services/PlayerService'
import RaceService from '../../../services/RaceService'
import TrainingService from '../../../services/TrainingService'
import UserService from '../../../services/UserService'
import { VehicleService } from '../../../services/VehicleService'
import { SearchRace } from '../searchRace/SearchRace'
import { MainChooseMap } from './chooseMap/MainChooseMap'
import './Header.css'
import { SearchUnavailableByDamage } from './searchUnavailableByDamage/SearchUnavailableByDamage'

interface HeaderProps {
    selectedMode: RaceTypes
    onModeChange?: (mode: RaceTypes) => void
    selectedVehicle: Vehicle | null
    setSelectedVehicle?: React.Dispatch<React.SetStateAction<Vehicle | null>>
    profile?: UserProfile | null
    setProfile?: React.Dispatch<React.SetStateAction<UserProfile | null>>
    title?: string
}

export const Header = ({ selectedMode, onModeChange, selectedVehicle, setSelectedVehicle, profile, setProfile, title }: HeaderProps) => {
    const [language, setLanguage] = useState<Language>('ru')
    const t = translations[language]
    const navigate = useNavigate()
    const [online, setOnline] = useState(1)
    const [showSearchRace, setShowSearchRace] = useState(true)
    const [isButtonActive, setIsButtonActive] = useState(!selectedVehicle?.inRace && !selectedVehicle?.damage)
    const [isModePending, setIsModePending] = useState(false)
    const [chooseMapVisible, setChooseMapVisible] = useState(false)

    const handlePlayClick = async () => {

        const raceServiceMethods = {
            [RaceTypes.UNRANKED]: RaceService.addPlayerToSearchUnRankedRace,
            [RaceTypes.RANKED]: RaceService.addPlayerToSearchRankedRace,
        }
        if (!showSearchRace) {

            if (selectedVehicle?.inRace) {
                toast.error(
                    t.vehicle_is_in_race,
                    { className: 'notification-error' }
                )
                return
            }

            if (selectedMode === RaceTypes.FREEROOM) {
                await PlayerService.runFreeroomCommon(selectedVehicle?.id as number)
                    .catch((error) => {
                        toast.error(
                            error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                                ? t[error.response.data as keyof typeof t]
                                : t.error_unknown,
                            { className: 'notification-error' }
                        )
                    })
            } else if (selectedMode === RaceTypes.TRAINING) {
                const newIsModePending = !isModePending
                setChooseMapVisible(newIsModePending)
                setIsModePending(newIsModePending)
            } else if (selectedMode === RaceTypes.GHOST) {
                await GhostService.startSearch(selectedVehicle?.id as number)
                    .then((response) => {
                        if (response.status === 200) {
                            setShowSearchRace(true)
                        }
                    })
                    .catch((error) => {
                        toast.error(
                            error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                                ? t[error.response.data as keyof typeof t]
                                : t.error_unknown,
                            { className: 'notification-error' }
                        )
                    })
            } else if (Object.keys(raceServiceMethods).includes(selectedMode)) {
                if ((selectedMode === RaceTypes.RANKED) && ![6, 8, 10].includes(selectedVehicle?.rank as number)) {
                    toast.error(
                        t.you_need_to_choose_vehicle_with_rank_6_8_or_10,
                        { className: 'notification-error' }
                    )
                    return
                }

                await raceServiceMethods[selectedMode](selectedVehicle?.id as number)
                    .then((response) => {
                        if (response.status === 200) {
                            setShowSearchRace(true)
                        }
                    })
                    .catch((error) => {
                        toast.error(
                            error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                                ? t[error.response.data as keyof typeof t]
                                : t.error_unknown,
                            { className: 'notification-error' }
                        )
                    })
            }
        } else {
            if (Object.keys(raceServiceMethods).includes(selectedMode)) {
                await RaceService.removePlayerFromRaceSearch()
                    .catch((error) => {
                        toast.error(
                            error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                                ? t[error.response.data as keyof typeof t]
                                : t.error_unknown,
                            { className: 'notification-error' }
                        )
                    })
                setShowSearchRace(false)
            } else if (selectedMode === RaceTypes.TRAINING) {
                stopTrainingSearch()
                setShowSearchRace(false)
            } else if (selectedMode === RaceTypes.GHOST) {
                stopGhostSearch()
                setShowSearchRace(false)
            }

        }
    }

    const runTrainingSearch = (map: string) => {
        if (!selectedVehicle || !selectedVehicle.id) {
            toast.error(
                t.vehicle_not_selected,
                { className: 'notification-error' }
            )
            return
        }

        if (showSearchRace) {
            toast.error(
                t.stop_search_first,
                { className: 'notification-error' }
            )
            return
        }

        TrainingService.startSearch(selectedVehicle?.id as number, map)
            .then((_response) => {
                setShowSearchRace(true)
                setIsModePending(false)
                setChooseMapVisible(false)
            })
            .catch((error) => {
                toast.error(
                    error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                        ? t[error.response.data as keyof typeof t]
                        : t.error_unknown,
                    { className: 'notification-error' }
                )
            })
    }

    const stopTrainingSearch = () => {
        TrainingService.stopSearch()
            .then((_response) => {
                setShowSearchRace(false)
            })
            .catch((error) => {
                toast.error(
                    error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                        ? t[error.response.data as keyof typeof t]
                        : t.error_unknown,
                    { className: 'notification-error' }
                )
            })
    }

    const stopGhostSearch = () => {
        GhostService.stopSearch()
            .then((_response) => {
                setShowSearchRace(false)
            })
            .catch((error) => {
                toast.error(
                    error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                        ? t[error.response.data as keyof typeof t]
                        : t.error_unknown,
                    { className: 'notification-error' }
                )
            })
    }


    useEffect(() => {
        const fetchUserProfile = async () => {
            await UserService.getUserProfile()
                .then((response) => {
                    if (!areProfilesEqual(profile ?? null, response)) {
                        setProfile?.(response)
                        setOnline(response.online)
                        localStorage.setItem('profile', JSON.stringify(response))
                    }
                })
                .catch((error) => {
                    toast.error(
                        error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                            ? t[error.response.data as keyof typeof t]
                            : t.error_unknown,
                        { className: 'notification-error' }
                    )
                })
        }

        if (!profile) {
            fetchUserProfile()
        }
    }, [profile])

    const starIcon = (
        <img src={'/icons/32px/xp.png'} alt="star" />
    )

    const moneyIcon = (
        <img src={'/icons/32px/money.png'} alt="coin" />
    )

    const coinIcon = (
        <img src={'/icons/32px/dp.png'} alt="coin" />
    )

    const modes = {
        freeroam: {
            [RaceTypes.FREEROOM]: t.free_mode,
        },
        ranked: {
            [RaceTypes.RANKED]: t.rating_race,
        },
        unranked: {
            [RaceTypes.UNRANKED]: t.random_race,
        },
        ghost: {
            [RaceTypes.GHOST]: t.ghost_race,
        },
        training: {
            [RaceTypes.TRAINING]: t.training
        }
    }

    const [playText, setPlayText] = useState(t.play)

    useEffect(() => {
        if (showSearchRace) {
            setPlayText(t.stop_search)
        } else if (isModePending) {
            setPlayText(t.cancel)
        } else {
            setPlayText(t.play)
        }
    }, [showSearchRace, isModePending])

    const handleResearchClick = () => {
        navigate('/research')
    }

    const handleModeSelect = (mode: RaceTypes) => {
        if (isModePending) {
            setIsModePending(false)
            setChooseMapVisible(false)
            return
        }
        if (showSearchRace) {
            toast.error(
                t.stop_search_first,
                { className: 'notification-error' }
            )
            return
        }

        localStorage.setItem('lastChosenActivity', mode)
        PlayerService.trySaveLastChosenActivity(mode)

        if (onModeChange) {
            onModeChange(mode)
        }
    }

    const fetchLastChoosenActivity = async () => {
        const lastChosenActivity = localStorage.getItem('lastChosenActivity')
        if (lastChosenActivity) {
            if (onModeChange) onModeChange(lastChosenActivity as RaceTypes)
        } else {
            const data = await PlayerService.tryFetchLastChosenActivityFromClient()
                .catch((error) => {
                    console.warn(error)
                })
            const activity = data?.activity
            if (activity) {
                localStorage.setItem('lastChosenActivity', String(activity))
                if (onModeChange) onModeChange(activity as RaceTypes)
            }
        }
    }

    useEffect(() => {
        fetchLastChoosenActivity()

        const token = localStorage.getItem('accessToken')
        const eventSource = UserService.getOnlineSSE(token as string || '')

        eventSource.addEventListener("INIT", (e) => {
            console.log("SSE connected:", e.data)
        })

        eventSource.addEventListener("ONLINE", (e) => {
            try {
                const online: number = JSON.parse(e.data)

                setOnline(online)
            } catch (err) {
                toast.error(
                    t.error_unknown,
                    { className: 'notification-error' }
                )
            }
        })

        eventSource.onerror = (err) => {
            eventSource.close()
        }

        return () => {
            eventSource.close()
        }
    }, [profile])

    const runFreeroam = () => {
        PlayerService.runFreeroomCommon(selectedVehicle?.id || 0)
            .catch((error) => {
                toast.error(
                    error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                        ? t[error.response.data as keyof typeof t]
                        : t.error_unknown,
                    { className: 'notification-error' }
                )
            })
    }

    useEffect(() => {
        const isActiveByRace = !selectedVehicle?.inRace
        const isActiveByDamageNotExist = !selectedVehicle?.damage
        const isActiveByDamage = isActiveByDamageNotExist
        setIsButtonActive(isActiveByRace && isActiveByDamage)
    }, [selectedVehicle])

    const handlePayoff = () => {
        VehicleService.payoffVehicleDamage(selectedVehicle?.id || 0)
            .then(() => {
                VehicleService.getVehicle(selectedVehicle?.id || 0)
                    .then((vehicle) => {
                        setSelectedVehicle?.(vehicle)
                        new Audio('/sounds/buy.mp3').play()
                        toast.success(t.vehicle_damage_paid_off, { className: 'notification-success' })
                        setProfile?.(null)
                    })
                    .catch((error) => {
                        toast.error(
                            error &&
                                error.response &&
                                error.response.data &&
                                t[error.response.data as keyof typeof t]
                                ? t[error.response.data as keyof typeof t]
                                : t.error_unknown,
                            { className: 'notification-error' }
                        )
                    })
            })
            .catch((error) => {
                toast.error(
                    error &&
                        error.response &&
                        error.response.data &&
                        t[error.response.data as keyof typeof t]
                        ? t[error.response.data as keyof typeof t]
                        : t.error_unknown,
                    { className: 'notification-error' }
                )
            })
    }

    const handleLogout = () => {
        AuthService.logout()
    }

    return (
        <>
            {<div className="header">
                <div className="header-content">
                    <div className="left-side">
                        <div className='header-container'>
                            <Logo type="main" scale={0.8} online={online} />
                        </div>
                        {profile && <div className="header-container">
                            <h3>{profile?.userName || '{username}'}</h3>
                            <p className='logout-button' onClick={handleLogout}>{t.logout}</p>
                        </div>}
                        <div>
                        </div>
                        {profile && <div className="header-container">
                            <Premium
                                name={profile?.subscription?.name || 'NONE'}
                                expiresAt={profile?.subscription?.expiresAt ? new Date(profile.subscription.expiresAt) : undefined}
                            />
                        </div>}
                    </div>
                    <div className="center">
                        {title ?
                            <h3 className='header-center-title'>{title}</h3> :
                            <>
                                <ModeSelector
                                    selectedMode={selectedMode}
                                    modes={modes}
                                    onModeChange={handleModeSelect}
                                />
                                <button
                                    className={`play-button ${showSearchRace || isModePending ? 'search-race-button' : 'play-button-font'} ${!isButtonActive ? 'in-race-button' : ''}`}
                                    onClick={handlePlayClick}
                                >
                                    {playText}
                                </button>
                                <div className="header-container" style={{ visibility: 'hidden', width: '200px' }}></div>
                            </>
                        }
                    </div>
                    <div className="right-side">
                        <div className="header-container-wrapper">
                            <div className="header-container">
                                <div className="top-container">
                                    <div className="icon-container">
                                        <div className="icon">
                                            {starIcon}
                                        </div>
                                    </div>
                                    {profile && <div className="text-container">
                                        <h3 className="text">{profile.experience.toFixed(0) || 0}</h3>
                                    </div>}
                                </div>
                                <div className="bottom-container">
                                    <a onClick={
                                        () => navigate('/donate?page=xp')
                                    }>{t.transfer}</a>
                                </div>
                            </div>
                            <div className="header-container">
                                <div className="top-container">
                                    <div className="icon-container">
                                        <div className="icon">
                                            {moneyIcon}
                                        </div>
                                    </div>
                                    {profile && <div className="text-container">
                                        <h3 className="text">{profile.balance.toFixed(0) || 0}</h3>
                                    </div>}
                                </div>
                                <div className="bottom-container">
                                    <a onClick={
                                        () => navigate('/donate?page=money')
                                    }>{t.transfer}</a>
                                </div>
                            </div>
                            <div className="header-container">
                                <div className="top-container">
                                    <div className="icon-container">
                                        <div className="icon">
                                            {coinIcon}
                                        </div>
                                    </div>
                                    {profile && <div className="text-container">
                                        <h3 className="text">{profile.donatePoints.toFixed(0) || 0}</h3>
                                    </div>}
                                </div>
                                <div className="bottom-container">
                                    <a onClick={
                                        () => navigate('/donate?page=main')
                                    }>{t.transfer}</a>
                                </div>
                            </div>
                        </div>
                    </div>
                    {
                        showSearchRace && (
                            <SearchRace
                                selectedVehicle={selectedVehicle}
                                withButton={['freeroam', 'cancel']}
                                setShow={setShowSearchRace}
                                onFreeroamButtonClick={() => runFreeroam()}
                                onCancelButtonClick={() => handlePlayClick?.()}
                            />
                        )
                    }
                </div>
                <div className="header-bottom">
                    <LongButton text={t.garage} onClick={() => navigate('/main')} />
                    <LongButton text={t.research} onClick={handleResearchClick} />
                    <LongButton text={t.mechanic} onClick={() => navigate(`/tuning?carId=${selectedVehicle?.id}`)} />
                    <LongButton text={t.shop} onClick={() => navigate('/donate')} />
                    <LongButton text={t.achievements} onClick={() => navigate('/achievements')} />
                    {false && <LongButton text={t.battlepass} onClick={() => navigate('/battlepass')} isComingSoon disabled />}
                </div>
                {selectedVehicle?.damage &&
                    <SearchUnavailableByDamage
                        damage={selectedVehicle?.damage}
                        onButtonClick={handlePayoff}
                    />
                }
            </div>
            }

            {chooseMapVisible && (
                <MainChooseMap
                    runTrainingSearch={runTrainingSearch}
                />
            )}
        </>
    )
}